import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  makeStyles,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import DateRangePicker from "components/input/dateRangePicker";
import SearchableSelect from "components/select/searchableSelect";
import { ViewWinnerContext } from "../../../contexts/ViewWinnerContext";
import { getLang } from "app/feature/constants";
import { GetApp as ExportIcon } from "@material-ui/icons";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  filterContainer: {
    backgroundColor: theme.palette.background.paper,
    display: "grid",
    gridTemplateColumns: "4fr 2fr 2fr 2fr 1fr 2fr",
    columnGap: theme.spacing(1),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      gridRowGap: theme.spacing(1),
      rowGap: theme.spacing(1),
    },
  },
  dropdownSearchBox: {
    backgroundColor: "#E9EDEF",
    borderRadius: 4,
    width: "100%",
    display: "flex",
    alignItems: "center",
  },
  onFocusBorder: {
    borderRadius: 4,
    outlineWidth: "1px",
    outlineStyle: "solid",
    outlineColor: theme.palette.secondary.main,
  },
  select: {
    minWidth: "unset",
    [theme.breakpoints.down("xs")]: {
      minWidth: "50%",
    },
  },
  divider: {
    height: "70%",
    placeSelf: "center",
  },
  textFieldInput: {
    padding: "7px",
    paddingTop: "8px",
    fontWeight: 500,
    color: theme.palette.primary.main,
    [theme.breakpoints.down("xs")]: {
      minWidth: "50%",
    },
  },
  inputRoot: {
    fontWeight: 500,
    paddingTop: "8px !important",
    paddingBottom: "7px !important",
    paddingLeft: "10px !important",
  },
  menuItemText: {
    padding: 10,
    fontWeight: 500,
  },
  emptyField: {
    "& .MuiInputBase-root": {
      color: "#A4A6A8",
    },
  },
  actionContainer: {
    padding: theme.spacing(2, 3),
    marginBottom: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
    alignItems: "center",
  },
  processedButton: {
    textTransform: "none",
    backgroundColor: "#6AAF68",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "#5a9458",
    },
  },
  exportButton: {
    textTransform: "none",
    border: "1px solid #D0D0D0",
    backgroundColor: "#ffffff",
    color: "#424242",
    "&:hover": {
      backgroundColor: "#F5F5F5",
    },
  },
}));

export default function ViewWinnerFilterPanelComponent() {
  const classes = useStyles();
  const [onFocus, setOnFocus] = useState(false);
  const {
    lang,
    filters,
    selectedRows,
    activeTab,
    isExporting,
    isUpdating,
    handleFilterChange,
    handleClearFilters,
    handleSearch,
    handleBatchProcess,
    handleExport,
  } = useContext(ViewWinnerContext);

  // Status options for filtering
  const statusOptions = [
    { name: "-", value: "" },
    { name: getLang(lang, "label.PROCESSED"), value: "Processed" },
    { name: getLang(lang, "label.UNPROCESSED"), value: "Unprocessed" },
    { name: getLang(lang, "label.REJECTED"), value: "Rejected" },
  ];

  // Prize Type options
  const prizeTypeOptions = [
    { name: "-", value: "" },
    { name: "Physical", value: 1 },
    { name: "Touch 'n Go", value: 2 },
    { name: "Loyalty Points", value: 4 },
  ];

  // Search type options
  const searchTypeOptions = [
    {
      name: getLang(lang, "label.PRIZE_NAME") || "Prize Name",
      value: "prizeName",
    },
    {
      name: getLang(lang, "label.WINNER_NAME") || "Winner Name",
      value: "winnerName",
    },
    {
      name: getLang(lang, "label.WINNER_EMAIL") || "Winner Email",
      value: "email",
    },
    {
      name: getLang(lang, "label.WINNER_PHONE_NUMBER") || "Winner Phone Number",
      value: "phone",
    },
  ];

  return (
    <Box>
      {/* Filter Container */}
      <Box className={classes.filterContainer}>
        {/* Combined Search Type Dropdown + Search Field */}
        <Box
          className={clsx(classes.dropdownSearchBox, {
            [classes.onFocusBorder]: onFocus,
          })}
        >
          <TextField
            style={{ minWidth: "unset" }}
            className={classes.select}
            select
            SelectProps={{
              MenuProps: {
                MenuListProps: {
                  style: { padding: 0 },
                },
                style: { marginTop: 5 },
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              },
            }}
            value={filters.searchType || "prizeName"}
            size="small"
            margin="none"
            InputProps={{
              disableUnderline: true,
              classes: { input: classes.inputRoot },
            }}
          >
            {searchTypeOptions.map((option, index) => (
              <MenuItem
                className={classes.menuItemText}
                value={option.value}
                key={index}
                onClick={() => {
                  handleFilterChange("searchType", option.value);
                }}
              >
                {option.name}
              </MenuItem>
            ))}
          </TextField>
          <Divider orientation="vertical" className={classes.divider} />
          <TextField
            fullWidth={true}
            onFocus={() => setOnFocus(true)}
            onBlur={() => setOnFocus(false)}
            size="small"
            margin="none"
            placeholder={getLang(lang, "placeholder.SEARCH")}
            InputProps={{
              disableUnderline: true,
              margin: "none",
            }}
            inputProps={{
              className: classes.textFieldInput,
            }}
            value={filters.searchText || ""}
            onChange={(e) => {
              handleFilterChange("searchText", e.target.value);
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
        </Box>

        {/* Status Dropdown */}
        <SearchableSelect
          value={
            filters.statuses
              ? statusOptions.find((v) => v.value === filters.statuses)
              : statusOptions[0]
          }
          handleChange={(value) => {
            handleFilterChange("statuses", value?.value || "");
          }}
          placeholder={getLang(lang, "placeholder.STATUS")}
          searchable={false}
          panelStyle={{ width: "180px" }}
          dropdownItem={statusOptions}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          isEmpty={!filters.statuses}
          returnType="object"
        />

        {/* Prize Type Dropdown */}
        <SearchableSelect
          value={
            filters.prizeType
              ? prizeTypeOptions.find((v) => v.value === filters.prizeType)
              : prizeTypeOptions[0]
          }
          handleChange={(value) => {
            handleFilterChange("prizeType", value?.value || "");
          }}
          placeholder={getLang(lang, "label.PRIZE_TYPE")}
          searchable={false}
          panelStyle={{ width: "180px" }}
          dropdownItem={prizeTypeOptions}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          isEmpty={!filters.prizeType}
          returnType="object"
        />

        {/* Date Range Picker */}
        <DateRangePicker
          label=""
          placeholder={
            activeTab === 1
              ? getLang(lang, "label.WON_DATE") || "Won Date"
              : getLang(lang, "label.CLAIMED_DATE") || "Claimed Date"
          }
          disableFuture={true}
          className={clsx({
            [classes.emptyField]: !filters.dateStart && !filters.dateEnd,
          })}
          value={
            filters.dateStart && filters.dateEnd
              ? { from: filters.dateStart, to: filters.dateEnd }
              : null
          }
          handleChange={(dateRange) => {
            if (dateRange) {
              handleFilterChange("dateStart", dateRange.from);
              handleFilterChange("dateEnd", dateRange.to);
            } else {
              handleFilterChange("dateStart", null);
              handleFilterChange("dateEnd", null);
            }
          }}
          InputPropStyle={{
            color:
              !filters.dateStart && !filters.dateEnd ? "#A4A6A8" : "#000000",
            fontWeight: "normal",
          }}
          inputProps={{
            fontWeight: "400",
            fontSize: "16px",
          }}
        />

        {/* Clear Button */}
        <Button
          variant="text"
          disableElevation
          color="secondary"
          size="small"
          onClick={handleClearFilters}
        >
          {getLang(lang, "label.CLEAR")}
        </Button>

        {/* Search Button */}
        <Button
          variant="contained"
          disableElevation
          color="primary"
          size="small"
          onClick={handleSearch}
        >
          {getLang(lang, "label.SEARCH")}
        </Button>
      </Box>
      {/* End of Filter Container */}

      {/* Action Buttons Container - Separate box for PROCESSED and Export buttons */}
      <Box className={classes.actionContainer}>
        {/* BULK PROCESS button - Show only in TO PROCESS tab */}
        {activeTab === 0 && (
          <Button
            variant="contained"
            onClick={handleBatchProcess}
            className={classes.processedButton}
            disabled={selectedRows.length === 0 || isUpdating}
            startIcon={
              isUpdating && <CircularProgress size={16} color="inherit" />
            }
            size="small"
          >
            {getLang(lang, "label.BULK_PROCESS")}
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={handleExport}
          className={classes.exportButton}
          startIcon={
            isExporting ? <CircularProgress size={16} /> : <ExportIcon />
          }
          disabled={isExporting}
          size="small"
        >
          {getLang(lang, "label.EXPORT")}
        </Button>
      </Box>
    </Box>
  );
}
